import React, { useState, useMemo } from 'react';
import CalcShell from '../CalcShell';
import { CalcNetPanel, Row, UnitInput, FeetInches, RadioGroup, ResultTable } from '../_fitnessHelpers';

const ACT = [
  { value: 1.2, label: 'Sedentary: little or no exercise' },
  { value: 1.375, label: 'Light: exercise 1-3 times/week' },
  { value: 1.55, label: 'Moderate: exercise 4-5 times/week' },
  { value: 1.725, label: 'Active: 6-7 sessions/week' },
  { value: 1.9, label: 'Very active: physical job' },
];
const GOALS = [
  { value: -500, label: 'Lose weight (0.5 kg/week)' },
  { value: 0, label: 'Maintain weight' },
  { value: 500, label: 'Gain weight (0.5 kg/week)' },
];

export default function CarbohydrateCalc() {
  const [tab, setTab] = useState(0);
  const [age, setAge] = useState(25);
  const [gender, setGender] = useState('male');
  const [ft, setFt] = useState(5);
  const [inches, setInches] = useState(10);
  const [lb, setLb] = useState(160);
  const [cm, setCm] = useState(178);
  const [kg, setKg] = useState(73);
  const [activity, setActivity] = useState(1.55);
  const [goal, setGoal] = useState(0);
  const [carbPct, setCarbPct] = useState(50);
  const clear = () => { setAge(25); setGender('male'); setFt(5); setInches(10); setLb(160); setCm(178); setKg(73); setActivity(1.55); setGoal(0); setCarbPct(50); };
  const c = useMemo(() => {
    const w = tab === 0 ? (+lb||0)*0.453592 : +kg||0;
    const h = tab === 0 ? ((+ft||0)*12+(+inches||0))*2.54 : +cm||0;
    const bmr = 10*w + 6.25*h - 5*age + (gender==='male'?5:-161);
    const tdee = bmr * activity;
    const target = tdee + goal;
    const carbCals = target * carbPct/100;
    const carbG = carbCals / 4;
    return { bmr, tdee, target, carbCals, carbG };
  }, [tab, age, gender, ft, inches, lb, cm, kg, activity, goal, carbPct]);

  return (
    <CalcShell look="cnet" title="Carbohydrate Calculator" subtitle="Estimate daily carbohydrate needs based on total energy and diet ratio.">
      <CalcNetPanel slug="carb" activeTab={tab} onTabChange={setTab} onClear={clear} right={
        <div>
          <div className="text-lg text-slate-800">Daily carbohydrate</div>
          <div className="text-2xl font-bold text-[#2f9c3f]" data-testid="carb-g">{c.carbG.toFixed(0)} g / day</div>
          <ResultTable testid="carb-detail-table" rows={[
            ['BMR', `${c.bmr.toFixed(0)} kcal`],
            ['TDEE (activity-adjusted)', `${c.tdee.toFixed(0)} kcal`],
            ['Daily target calories', <span className="font-bold text-[#2f9c3f]">{c.target.toFixed(0)} kcal</span>],
            [`Calories from carbs (${carbPct}%)`, `${c.carbCals.toFixed(0)} kcal`],
          ]}/>
        </div>
      }>
        <Row label="Age"><UnitInput value={age} onChange={setAge} testid="carb-age"/></Row>
        <Row label="Gender"><RadioGroup name="carb-g" value={gender} onChange={setGender} options={[{value:'male',label:'male'},{value:'female',label:'female'}]}/></Row>
        {tab === 0 ? <>
          <Row label="Height"><div className="grid grid-cols-2 gap-2 flex-1"><FeetInches ft={ft} inches={inches} onFt={setFt} onIn={setInches} testidPrefix="carb-h"/></div></Row>
          <Row label="Weight"><UnitInput value={lb} onChange={setLb} unit="pounds" testid="carb-w"/></Row>
        </> : <>
          <Row label="Height"><UnitInput value={cm} onChange={setCm} unit="cm" testid="carb-hcm"/></Row>
          <Row label="Weight"><UnitInput value={kg} onChange={setKg} unit="kg" testid="carb-wkg"/></Row>
        </>}
        <Row label="Activity"><select value={activity} onChange={e => setActivity(+e.target.value)} className="flex-1 px-3 py-2 bg-white border border-slate-400 rounded-sm text-sm" data-testid="carb-activity">{ACT.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}</select></Row>
        <Row label="Goal"><select value={goal} onChange={e => setGoal(+e.target.value)} className="flex-1 px-3 py-2 bg-white border border-slate-400 rounded-sm text-sm" data-testid="carb-goal">{GOALS.map(g=><option key={g.value} value={g.value}>{g.label}</option>)}</select></Row>
        <Row label="Carb %"><UnitInput value={carbPct} onChange={setCarbPct} unit="%" testid="carb-pct"/></Row>
      </CalcNetPanel>
    </CalcShell>
  );
}
